import React, {useState, useContext, useEffect} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {oneDark, oneLight} from "react-syntax-highlighter/dist/esm/styles/prism";
import SyntaxHighlighter from "../../utils/registerApex";
import StyleContext from "../../contexts/StyleContext";

const PostEditor = ({initial, mode, onSave, onCancel, busy, error}) => {
  const {isDark} = useContext(StyleContext);
  const [form, setForm] = useState(initial);
  const [tab, setTab] = useState("write"); // write | preview

  // CRITICAL: re-sync local state whenever the parent supplies new `initial`
  // (e.g. admin clicks "Edit" on a different post, or the parent finishes
  // fetching full post content asynchronously). Without this useEffect the
  // edit form would always show whatever was passed at mount-time and look
  // blank for posts whose content was hydrated late.
  useEffect(() => {
    setForm(initial || {});
    setTab("write");
  }, [initial]);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = e => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form className={`editor ${isDark ? "dark" : ""}`} onSubmit={submit}>
      {/* ---------- Top bar ---------- */}
      <div className="editor__topbar">
        <button type="button" className="editor__icon-btn" onClick={onCancel} title="Back">
          ← Posts
        </button>
        <div className="editor__topbar-status">
          {mode === "edit" ? "Editing post" : "New post"}
        </div>
        <div className="editor__topbar-actions">
          <div className="editor__seg">
            <button
              type="button"
              className={tab === "write" ? "active" : ""}
              onClick={() => setTab("write")}
            >
              Write
            </button>
            <button
              type="button"
              className={tab === "preview" ? "active" : ""}
              onClick={() => setTab("preview")}
            >
              Preview
            </button>
          </div>
          <button type="submit" className="editor__publish" disabled={busy}>
            {busy ? "Saving…" : mode === "edit" ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      {error && <p className="modal__error editor__error">{error}</p>}

      {/* ---------- Two-column body ---------- */}
      <div className="editor__layout">
        <div className="editor__main">
          {tab === "write" ? (
            <>
              <input
                type="text"
                className="editor__title"
                placeholder="Post title…"
                value={form.title}
                onChange={e => set("title", e.target.value)}
                required
              />
              <textarea
                className="editor__body"
                placeholder="Tell your story… (Markdown supported — use ```apex / ```javascript / ```html for code blocks)"
                value={form.content}
                onChange={e => set("content", e.target.value)}
                required
              />
            </>
          ) : (
            <article className="editor__preview blog-detail__body">
              <h1 style={{margin: "0 0 16px"}}>{form.title || "Untitled"}</h1>
              {form.cover_image && (
                <img className="blog-detail__cover" src={form.cover_image} alt="" />
              )}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || "");
                    const lang = (match ? match[1] : "").toLowerCase();
                    if (inline) return <code className={className} {...props}>{children}</code>;
                    return (
                      <SyntaxHighlighter
                        style={isDark ? oneDark : oneLight}
                        language={lang || "javascript"}
                        PreTag="div"
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    );
                  },
                }}
              >
                {form.content || "_Start writing to see a preview…_"}
              </ReactMarkdown>
            </article>
          )}
        </div>

        <aside className="editor__sidebar">
          <div className="editor__panel">
            <h4>Post settings</h4>
            <label className="editor__field">
              <span className="rdf-label">Excerpt</span>
              <textarea
                className="rdf-input rdf-textarea"
                rows="3"
                value={form.excerpt || ""}
                onChange={e => set("excerpt", e.target.value)}
                placeholder="One-line summary shown on the listing page."
              />
            </label>

            <label className="editor__field">
              <span className="rdf-label">Category</span>
              <input
                type="text"
                className="rdf-input"
                value={form.category || ""}
                onChange={e => set("category", e.target.value)}
                placeholder="e.g. Apex, LWC, DevOps"
              />
            </label>

            <label className="editor__field">
              <span className="rdf-label">Sort order</span>
              <input
                type="number"
                className="rdf-input"
                value={form.sort_order ?? ""}
                onChange={e => set(
                  "sort_order",
                  e.target.value === "" ? "" : parseInt(e.target.value, 10)
                )}
                placeholder="Auto on create — edit to reorder"
                min="0"
                step="1"
              />
              <small className="rdf-hint">
                Higher numbers appear first when sorting by order.
              </small>
            </label>

            <label className="editor__field">
              <span className="rdf-label">Cover image URL</span>
              <input
                type="url"
                className="rdf-input"
                value={form.cover_image || ""}
                onChange={e => set("cover_image", e.target.value)}
                placeholder="https://…"
              />
            </label>
            {form.cover_image && (
              <div className="editor__cover-preview">
                <img src={form.cover_image} alt="cover preview" />
              </div>
            )}
          </div>

          <div className="editor__panel">
            <h4>Tips</h4>
            <ul className="editor__tips">
              <li>Use <code>```apex</code> for Apex code blocks.</li>
              <li>Paste a YouTube/Vimeo URL as a markdown link to embed it.</li>
              <li>Use <code>##</code> for headings, <code>&gt;</code> for blockquotes.</li>
            </ul>
          </div>
        </aside>
      </div>
    </form>
  );
};

export default PostEditor;
